/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import KnowledgeHubApi, {
  type KnowledgeHubApiQueryParams,
} from 'embercom/lib/knowledge-hub/list-api';
import { getOwner } from '@ember/application';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import { FolderFilterOption } from 'embercom/lib/content-service/search-api';
import { type SortDirection } from 'embercom/lib/knowledge-hub/constants';
import { action } from '@ember/object';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type AllContent from 'embercom/controllers/apps/app/knowledge-hub/all-content';
import type Transition from '@ember/routing/transition';

export default class AllContentRoute extends Route {
  @service declare appService: AppService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare intercomEventService: $TSFixMe;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;

  queryParams = {
    nativeContent: {
      refreshModel: true,
    },
  };

  get app() {
    return this.appService.app;
  }

  async model(params: KnowledgeHubApiQueryParams): Promise<KnowledgeHubApi> {
    let defaultSearchParams = {
      app_id: this.appService.app.id as string,
      content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
      folder_filter_option: FolderFilterOption.None,
      object_types: this.knowledgeHubService.getSearchObjectTypes(),
      sort_by: 'title',
      sort_direction: 'asc' as SortDirection,
      ...(this.appService.app.canUseUnifiedContentUI && {
        native_content: false,
      }),
    };

    this.knowledgeHubService.fetchKnowledgeUsageSummary();
    this.knowledgeHubService.api = new KnowledgeHubApi(getOwner(this), defaultSearchParams, params);
    this.knowledgeHubService.api.loadPage();

    return this.knowledgeHubService.api;
  }

  setupController(controller: AllContent, model: KnowledgeHubApi, transition: Transition) {
    super.setupController(controller, model, transition);

    if (this.appService.app.canUseUnifiedContentUI) {
      // Set default value for nativeContent if not explicitly set via query param
      if (controller.nativeContent === undefined) {
        controller.set('nativeContent', false);
      }
    }
  }

  @action
  didTransition() {
    this.intercomEventService.trackEvent('knowledge-hub-content-page-visited');
  }

  resetController(controller: AllContent, isExiting: boolean) {
    if (isExiting) {
      this.knowledgeHubDrawerEditorService.removeEmberStickyQueryParams(controller);
      controller.set('openSalesforceSync', undefined);
      controller.set('salesforceSyncStep', undefined);
    }
  }
}
